import {
  submitVersionInputSchema,
  type BriefAsset,
  type BriefDetails,
  type DeliverableDetails,
  type SubmitVersionInput,
  type TaskDetail,
  type Version,
} from '@mybusinessmate/schema';
import { toScheduledAt, type Platform } from './task-form';

/** A section of the site as the form holds it. `image` is the screenshot that shows it is built. */
export interface SectionValue {
  title: string;
  kind: string;
  description: string;
  built: boolean;
  image?: BriefAsset;
}

export interface SubmitValues {
  notes: string;
  /** The task's slider, moved with the version. */
  progress: number;
  platforms: Platform[];
  caption: string;
  hashtags: string[];
  audioNote: string;
  publishDate: string;
  publishTime: string;
  /** A reel's video link, for one too big to upload. */
  videoUrl: string;
  liveUrl: string;
  sslStatus: 'Active' | 'Pending' | 'Not Started';
  adminPanelUrl: string;
  adminUsername: string;
  primaryColor: string;
  secondaryColor: string;
  logoNotes: string;
  sections: SectionValue[];
  pageUrl: string;
  resolvedNotes: string;
}

export type SubmitErrors = Record<string, string>;

const platformsOf = (list: string[]): Platform[] =>
  list.filter((p): p is Platform => p === 'instagram' || p === 'facebook');

const pad = (n: number) => String(n).padStart(2, '0');
/** A stored instant as the date and time inputs, in the browser's timezone. */
function splitScheduled(iso: string | null) {
  if (!iso) return { date: '', time: '' };
  const at = new Date(iso);
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  };
}

export const emptySubmitValues = (progress = 0): SubmitValues => ({
  notes: '',
  progress,
  platforms: [],
  caption: '',
  hashtags: [],
  audioNote: '',
  publishDate: '',
  publishTime: '',
  videoUrl: '',
  liveUrl: '',
  sslStatus: 'Not Started',
  adminPanelUrl: '',
  adminUsername: '',
  primaryColor: '',
  secondaryColor: '',
  logoNotes: '',
  sections: [],
  pageUrl: '',
  resolvedNotes: '',
});

/** What a new version starts from: the brief's own plan (where it posts, when, which colours, which sections). */
function fromBrief(values: SubmitValues, details: BriefDetails): SubmitValues {
  switch (details.type) {
    case 'post':
    case 'reel': {
      const when = splitScheduled(details.scheduledUploadAt);
      return {
        ...values,
        platforms: platformsOf(details.platforms),
        caption: details.caption ?? '',
        hashtags: details.hashtags,
        audioNote: details.audioNote ?? '',
        publishDate: when.date,
        publishTime: when.time,
      };
    }
    case 'website':
      return {
        ...values,
        primaryColor: details.primaryColor ?? '',
        secondaryColor: details.secondaryColor ?? '',
        logoNotes: details.logoNotes ?? '',
        // The brief's pages list what to build: each section of each page starts as an unticked row.
        sections: details.pages.flatMap((page) =>
          page.sections.map((title) => ({ title, kind: '', description: '', built: false })),
        ),
      };
    case 'monthly_update':
      return { ...values, pageUrl: details.pageUrl ?? '' };
    case 'custom':
      return values;
  }
}

/** A version's own fields (the same shape the API stores), as the form holds them. */
export function fromDeliverable(values: SubmitValues, details: DeliverableDetails): SubmitValues {
  switch (details.type) {
    case 'post':
    case 'reel': {
      const when = splitScheduled(details.scheduledUploadAt);
      return {
        ...values,
        platforms: platformsOf(details.platforms),
        caption: details.caption ?? '',
        hashtags: details.hashtags,
        audioNote: details.audioNote ?? '',
        publishDate: when.date,
        publishTime: when.time,
        videoUrl: details.type === 'reel' ? (details.videoUrl ?? '') : '',
      };
    }
    case 'website':
      return {
        ...values,
        liveUrl: details.liveUrl ?? '',
        sslStatus:
          details.sslStatus === 'Active' || details.sslStatus === 'Pending' ? details.sslStatus : 'Not Started',
        adminPanelUrl: details.adminPanelUrl ?? '',
        adminUsername: details.adminUsername ?? '',
        primaryColor: details.primaryColor ?? '',
        secondaryColor: details.secondaryColor ?? '',
        logoNotes: details.logoNotes ?? '',
        sections: details.sections.map((s) => ({
          title: s.title,
          kind: s.kind ?? '',
          description: s.description ?? '',
          built: s.built,
          image: s.image ?? undefined,
        })),
      };
    case 'monthly_update':
      return { ...values, pageUrl: details.pageUrl ?? '', resolvedNotes: details.resolvedNotes ?? '' };
    case 'custom':
      return values;
  }
}

/**
 * Where the submit page starts: the open draft if there is one (so "Save as draft" picks up where it stopped),
 * otherwise the latest version (a new version is the last one, changed), otherwise the brief's plan.
 */
export function initialSubmitValues(task: TaskDetail, versions: Version[]): { values: SubmitValues; draft?: Version } {
  const draft = versions.find((v) => v.internalStatus === 'draft');
  const base = emptySubmitValues(task.progress);
  if (draft) return { values: { ...fromDeliverable(base, draft.details), notes: draft.notes ?? '' }, draft };
  const latest = versions[0];
  if (latest) return { values: fromDeliverable(base, latest.details) };
  return { values: task.brief ? fromBrief(base, task.brief.details) : base };
}

/** Blank optional fields are left out rather than sent as '' (the API treats a sent value as meant). */
const blank = (value: string) => (value.trim() ? value.trim() : undefined);

export function deliverableFrom(values: SubmitValues, type: TaskDetail['taskType']): SubmitVersionInput['deliverable'] {
  switch (type) {
    case 'post':
      return {
        type,
        caption: blank(values.caption),
        hashtags: values.hashtags,
        platforms: values.platforms,
        audioNote: blank(values.audioNote),
        scheduledUploadAt: toScheduledAt(values.publishDate, values.publishTime),
      };
    case 'reel':
      return {
        type,
        caption: blank(values.caption),
        hashtags: values.hashtags,
        platforms: values.platforms,
        audioNote: blank(values.audioNote),
        scheduledUploadAt: toScheduledAt(values.publishDate, values.publishTime),
        videoUrl: blank(values.videoUrl),
      };
    case 'website':
      return {
        type,
        liveUrl: blank(values.liveUrl),
        sslStatus: values.sslStatus,
        adminPanelUrl: blank(values.adminPanelUrl),
        adminUsername: blank(values.adminUsername),
        primaryColor: blank(values.primaryColor),
        secondaryColor: blank(values.secondaryColor),
        logoNotes: blank(values.logoNotes),
        sections: values.sections.map((s) => ({
          title: s.title,
          kind: blank(s.kind),
          description: blank(s.description),
          built: s.built,
          image: s.image,
        })),
      };
    case 'monthly_update':
      return { type, pageUrl: blank(values.pageUrl), resolvedNotes: blank(values.resolvedNotes) };
    case 'custom':
      return { type };
  }
}

export type SubmitResult = { ok: true; body: SubmitVersionInput } | { ok: false; errors: SubmitErrors };

/**
 * Validates with the schema the API uses, so the page never disagrees with the server about what a version needs.
 * `progress` only travels with a version that is sent: a draft leaves the task's slider alone.
 */
export function toSubmitRequest(
  values: SubmitValues,
  type: TaskDetail['taskType'],
  assets: BriefAsset[],
  draft: boolean,
): SubmitResult {
  const body: SubmitVersionInput = {
    draft,
    notes: values.notes,
    progress: draft ? undefined : values.progress,
    assets,
    deliverable: deliverableFrom(values, type),
  };
  const parsed = submitVersionInputSchema.safeParse(body);
  if (parsed.success) return { ok: true, body };

  const errors: SubmitErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
}
